import { Injectable } from '@angular/core';
import { Photo } from '@capacitor/camera';
import { Filesystem } from '@capacitor/filesystem';

/**
 * Encode les photos prises par la caméra, pour l'envoi au backend comme pour l'affichage local.
 *
 * Sur appareil, le fichier est lu directement par l'API Filesystem, comme le prévoit
 * `CameraResultType.Uri`. L'implémentation précédente passait par `fetch(webPath)`, donc par le
 * serveur local de la WebView, et ne gérait pas le rejet de `response.blob()` : la promesse
 * restait alors en suspens pour toujours et l'envoi tournait sans fin, sans erreur ni requête.
 */
@Injectable({
  providedIn: 'root',
})
export class PhotoEncoderService {
  /** Data URLs complètes, directement affichables dans un `<img src>`. */
  public async encoderEnDataUrl(photos: Photo[] | undefined): Promise<string[]> {
    if (!photos || photos.length === 0) {
      return [];
    }
    return Promise.all(photos.map(photo => this.encoderUne(photo)));
  }

  /** Charge utile base64 attendue par le backend, sans le préfixe de data URL. */
  public async encoder(photos: Photo[] | undefined): Promise<string[]> {
    return (await this.encoderEnDataUrl(photos)).map(donnees => this.chargeUtile(donnees));
  }

  /** Retire le préfixe `data:<type>;base64,` d'une data URL. */
  public chargeUtile(dataUrl: string): string {
    return dataUrl.substring(dataUrl.indexOf(',') + 1);
  }

  private async encoderUne(photo: Photo): Promise<string> {
    if (photo.path) {
      const { data } = await Filesystem.readFile({ path: photo.path });
      // `Filesystem` rend du base64 nu : on reconstruit la data URL, sans quoi les vignettes
      // de la main courante ne s'afficheraient pas.
      return typeof data === 'string' ? `data:image/${photo.format || 'jpeg'};base64,${data}` : this.blobEnDataUrl(data);
    }
    if (!photo.webPath) {
      throw new Error("La photo n'expose ni chemin de fichier ni URL web");
    }
    const reponse = await fetch(photo.webPath);
    return this.blobEnDataUrl(await reponse.blob());
  }

  private blobEnDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const lecteur = new FileReader();
      lecteur.onloadend = () => resolve(typeof lecteur.result === 'string' ? lecteur.result : '');
      lecteur.onerror = () => reject(lecteur.error ?? new Error('Lecture du fichier impossible'));
      lecteur.readAsDataURL(blob);
    });
  }
}
